import { Link } from "react-router-dom";
import { TopNav, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import {
  ShoppingBag, CalendarCheck, ArrowRight, ShieldCheck, FileText,
  Sparkles, Lock, LogIn,
} from "lucide-react";

const PURCHASE_URL = process.env.REACT_APP_PURCHASE_URL || "";
const BOOKING_URL = process.env.REACT_APP_BOOKING_URL || "";

const Benefit = ({ icon: Icon, title, body, testId }) => (
  <div className="kcis-card p-6" data-testid={testId}>
    <div className="h-10 w-10 rounded-xl bg-purple-50 text-purple-900 flex items-center justify-center">
      <Icon className="h-5 w-5" />
    </div>
    <h3 className="font-display text-lg text-slate-900 mt-3">{title}</h3>
    <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{body}</p>
  </div>
);

const PrimaryAction = ({ icon: Icon, title, body, url, testId }) => {
  if (!url) {
    return (
      <div className="kcis-card p-6 flex flex-col items-start gap-2 bg-slate-50/40" data-testid={`${testId}-disabled`}>
        <div className="h-10 w-10 rounded-xl bg-slate-100 text-slate-400 flex items-center justify-center">
          <Icon className="h-5 w-5" />
        </div>
        <h3 className="font-display text-lg text-slate-700">{title}</h3>
        <p className="text-sm text-slate-500 mt-1 leading-relaxed">{body}</p>
        <span className="text-xs text-slate-400 mt-2 italic">Currently unavailable — please check back soon.</span>
      </div>
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="kcis-card p-6 flex flex-col items-start gap-2 hover:border-purple-300 transition group"
      data-testid={testId}
    >
      <div className="h-10 w-10 rounded-xl bg-purple-50 text-purple-900 flex items-center justify-center">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="font-display text-lg text-slate-900 group-hover:text-purple-900">{title}</h3>
      <p className="text-sm text-slate-600 mt-1 leading-relaxed">{body}</p>
      <span className="inline-flex items-center text-sm text-purple-900 font-medium mt-2">
        Continue <ArrowRight className="h-4 w-4 ml-1" />
      </span>
    </a>
  );
};

export default function Start() {
  return (
    <div className="min-h-screen flex flex-col">
      <TopNav />
      <main className="flex-1 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 w-full" data-testid="start-page">
        {/* Hero */}
        <div className="kcis-fade-up">
          <div className="kcis-overline text-purple-900">Begin your KCIS journey</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 mt-3">
            Holistic trichology, guided by <span className="text-purple-900">clinical intelligence</span>
          </h1>
          <p className="text-base text-slate-600 mt-5 max-w-2xl leading-relaxed">
            KCIS — the KISSED Clinical Intelligence System — combines a comprehensive wellness intake
            with a structured, science-backed protocol report so you can take meaningful, informed steps
            toward your hair and overall wellness goals.
          </p>
        </div>

        {/* Educational disclaimer */}
        <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50/60 p-5 flex items-start gap-3" data-testid="start-disclaimer">
          <ShieldCheck className="h-5 w-5 text-purple-900 mt-0.5 shrink-0" />
          <p className="text-xs text-slate-600 leading-relaxed">
            <span className="font-semibold text-slate-800">Educational use only.</span> KCIS is a holistic
            wellness education and tracking tool. It does not diagnose, treat, cure, or prevent any
            disease, and is not a substitute for personalized medical advice from a licensed clinician.
            Always consult your healthcare provider for medical concerns.
          </p>
        </div>

        {/* Benefits */}
        <section className="mt-12">
          <div className="kcis-overline">What you'll get</div>
          <h2 className="font-display text-2xl tracking-tight text-slate-900 mt-1">Inside your assessment</h2>
          <div className="grid sm:grid-cols-3 gap-4 mt-5">
            <Benefit icon={FileText} title="Wellness intake" body="A thorough, non-diagnostic intake covering hair, scalp, lifestyle, stress, sleep, hydration, and more." testId="start-benefit-intake" />
            <Benefit icon={Sparkles} title="Science-Backed Protocol Report" body="A structured, branded protocol summary across 8 sections — generated for you in seconds." testId="start-benefit-report" />
            <Benefit icon={CalendarCheck} title="7-day follow-up" body="A simple check-in window to track your progress and refine your protocol going forward." testId="start-benefit-followup" />
          </div>
        </section>

        {/* Primary actions */}
        <section className="mt-12">
          <div className="kcis-overline">Choose how to begin</div>
          <h2 className="font-display text-2xl tracking-tight text-slate-900 mt-1">Next steps</h2>
          <div className="grid md:grid-cols-2 gap-4 mt-5">
            <PrimaryAction
              icon={ShoppingBag}
              title="Purchase assessment"
              body="Unlock your KCIS™ intake + branded wellness report. Secure checkout."
              url={PURCHASE_URL}
              testId="start-cta-purchase"
            />
            <PrimaryAction
              icon={CalendarCheck}
              title="Book a clinical consultation"
              body="See your KCIS practitioner in-clinic or virtually for a personalised review."
              url={BOOKING_URL}
              testId="start-cta-booking"
            />
          </div>
        </section>

        {/* Already have access */}
        <section className="mt-10 mb-2">
          <div className="kcis-card p-6 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-purple-50 text-purple-900 flex items-center justify-center">
                <Lock className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-display text-base text-slate-900">Already have access?</h3>
                <p className="text-xs text-slate-500 mt-0.5">Sign in to view your protocol report and follow-ups.</p>
              </div>
            </div>
            <Link to="/client/login">
              <Button variant="outline" className="rounded-full border-purple-300 text-purple-900 hover:bg-purple-50" data-testid="start-client-portal-link">
                <LogIn className="h-4 w-4 mr-2" /> Open client portal
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
